import { useOnboarding } from "@onboardjs/react";
import { useMutation, useQuery } from "convex/react";
import { AlertCircle, CheckCircle2, Globe, Loader2 } from "lucide-react";
import { useState } from "react";
import { api } from "../../../convex/_generated/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";

export function DomainDiscoveryStep() {
	const { next, updateContext } = useOnboarding();
	const currentSession = useQuery(api.lib.viewer.getCurrentSession);
	const discovery = useQuery(api.codex.queries.getDiscovery);
	const startDiscovery = useMutation(api.codex.mutations.startDiscovery);

	const [websiteUrl, setWebsiteUrl] = useState("");
	const [isStarting, setIsStarting] = useState(false);

	// The firm's canonical domain comes from the signed-in user's email (set by
	// `firmAutoProvision.ensureActiveFirm`).
	const orgDomain = currentSession?.tenantDomain;

	const effectiveUrl =
		websiteUrl ||
		discovery?.websiteUrl ||
		(orgDomain ? `https://${orgDomain}` : "");

	const isDiscoveryComplete = discovery?.status === "complete";
	const isStale =
		discovery?.updatedAt &&
		(discovery.status === "crawling" || discovery.status === "extracting") &&
		Date.now() - discovery.updatedAt > 5 * 60 * 1000;
	const isDiscoveryRunning =
		!isStale &&
		(discovery?.status === "crawling" || discovery?.status === "extracting");
	const isDiscoveryFailed = discovery?.status === "failed" || isStale;

	const handleStartDiscovery = async () => {
		if (!effectiveUrl) return;
		setIsStarting(true);
		try {
			const url = effectiveUrl.startsWith("http")
				? effectiveUrl
				: `https://${effectiveUrl}`;
			await startDiscovery({ websiteUrl: url });
		} finally {
			setIsStarting(false);
		}
	};

	const handleContinue = async () => {
		if (discovery?.extractedFirm) {
			await updateContext({
				flowData: {
					extractedFirm: discovery.extractedFirm,
					extractedTeam: discovery.extractedTeam,
				},
			});
		}
		await next();
	};

	return (
		<div className="flex flex-col gap-6">
			{/* Organization Info */}
			<div className="flex items-center gap-3 rounded-lg border p-4">
				<div className="flex h-10 w-10 items-center justify-center rounded-full bg-primary/10">
					<Globe className="h-5 w-5 text-primary" />
				</div>
				<div>
					<p className="font-medium">
						{currentSession?.tenantName ?? "Your Organization"}
					</p>
					{orgDomain && (
						<p className="text-sm text-muted-foreground">{orgDomain}</p>
					)}
				</div>
			</div>

			{/* Website URL Input */}
			<div className="flex flex-col gap-2">
				<Label htmlFor="website-url">Firm Website</Label>
				<div className="flex gap-2">
					<Input
						id="website-url"
						type="url"
						placeholder="https://yourfirm.com"
						value={effectiveUrl}
						onChange={(e) => setWebsiteUrl(e.target.value)}
						disabled={isDiscoveryRunning}
					/>
					{!isDiscoveryComplete && !isDiscoveryRunning && (
						<Button
							onClick={handleStartDiscovery}
							disabled={!effectiveUrl || isStarting}
						>
							{isStarting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
							Discover
						</Button>
					)}
				</div>
			</div>

			{/* Discovery Status */}
			{isDiscoveryRunning && (
				<div className="flex items-center gap-3 rounded-lg border border-primary/30 bg-primary/10 p-4">
					<Loader2 className="h-5 w-5 animate-spin text-primary" />
					<div>
						<p className="font-medium text-foreground">
							{discovery?.status === "crawling"
								? "Crawling your website..."
								: "Extracting firm information..."}
						</p>
						<p className="text-muted-foreground text-sm">
							This may take a moment. We are analyzing your firm's website to
							pre-fill your profile.
						</p>
					</div>
				</div>
			)}

			{isDiscoveryComplete && (
				<div className="flex items-center gap-3 rounded-lg border border-success/30 bg-success/10 p-4">
					<CheckCircle2 className="h-5 w-5 text-success" />
					<div>
						<p className="font-medium text-foreground">
							Company data discovered
						</p>
						<p className="text-muted-foreground text-sm">
							We found your firm's information. You can review and edit it in
							the next steps.
						</p>
					</div>
				</div>
			)}

			{isDiscoveryFailed && (
				<div className="flex items-center gap-3 rounded-lg border border-destructive/30 bg-destructive/10 p-4">
					<AlertCircle className="h-5 w-5 text-destructive" />
					<div>
						<p className="font-medium text-foreground">Discovery failed</p>
						<p className="text-muted-foreground text-sm">
							{discovery?.error ??
								(isStale
									? "Discovery appears to have stalled. You can retry or continue to fill in details manually."
									: "An error occurred. You can try again or continue to fill in details manually.")}
						</p>
					</div>
					<Button variant="outline" size="sm" onClick={handleStartDiscovery}>
						Retry
					</Button>
				</div>
			)}

			{/* Continue */}
			<div className="flex justify-end pt-2">
				<Button onClick={handleContinue} disabled={isDiscoveryRunning}>
					{isDiscoveryComplete || isDiscoveryFailed
						? "Continue"
						: "Skip & Continue"}
				</Button>
			</div>
		</div>
	);
}
